import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { RoomType, type FurnitureCategory } from "../../types/C1_ModelTypes";
import type { C1_Language } from "../../C1_language";
import c1_state from "../../model/C1_State";
import { C1_FurnitureIcon, C1_KitchenApplianceSymbol, type C1_KitchenApplianceIconKind } from "../C1_FurnitureIcon";
import { C1_FurnitureSvg, C1_FurnitureUprightContent } from "../C1_FurnitureSvg";

const DEFAULT_WIDTH = 0.6;
const DEFAULT_HEIGHT = 0.6;

type BoxKitchenApplianceOptions = {
  id: string;
  iconKind: C1_KitchenApplianceIconKind;
  description: Record<C1_Language, string>;
  centerLabel: Record<C1_Language, string>;
  category?: FurnitureCategory;
  additionalCategories?: FurnitureCategory[];
};

export function createBoxKitchenAppliance({
  id,
  iconKind,
  description,
  centerLabel,
  category = RoomType.Kitchen,
  additionalCategories,
}: BoxKitchenApplianceOptions): C1_FurnitureDefinition {
  return {
    id,
    icon: <C1_FurnitureIcon kind={iconKind} />,
    description,
    category,
    additionalCategories,
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: DEFAULT_HEIGHT,
    autoRotate: true,
    autoReflect: true,
    grabsW: true,
    grabsH: false,
    properties: [],
    Renderer: ({
      width,
      height,
      rotation,
      PX,
      strokeWidth,
      strokeColor,
      fillColor,
      showDetails,
      ownerRotation,
      ownerReflect,
    }: C1_FurnitureRendererProps) => {
      const finalRotation = rotation ?? 0;
      const finalStrokeWidth = strokeWidth ?? 2;
      const finalStrokeColor = strokeColor ?? "#000000";
      const finalFillColor = fillColor ?? "#ffffff";
      const label = centerLabel[c1_state.language];
      const inset = 0.02 * PX;
      const detailX = Math.min(width * 0.14, 0.08 * PX);
      const iconSize = Math.min(22, width * 0.42, height * 0.42);

      return (
        <C1_FurnitureSvg
          PX={PX}
          width={width}
          height={height}
          rotation={finalRotation}
          viewBoxWidth={width}
          viewBoxHeight={height}
          originXPercentage={0}
          originYPercentage={0}
        >
          <g>
            <rect
              x={inset}
              y={inset}
              width={Math.max(0, width - inset * 2)}
              height={Math.max(0, height - inset * 2)}
              rx={inset}
              fill={finalFillColor}
              stroke={finalStrokeColor}
              strokeWidth={finalStrokeWidth * 2}
            />
            {showDetails && (
              <line
                x1={detailX}
                y1={inset * 4}
                x2={detailX}
                y2={height - inset * 4}
                stroke={finalStrokeColor}
                strokeWidth={finalStrokeWidth}
              />
            )}
          </g>
          <C1_FurnitureUprightContent
            anchorX={width / 2}
            anchorY={height / 2}
            ownerRotation={Number(ownerRotation ?? finalRotation)}
            ownerReflect={Boolean(ownerReflect)}
          >
            <svg
              x={width / 2 - iconSize / 2}
              y={height * 0.13}
              width={iconSize}
              height={iconSize}
              viewBox="0 0 24 24"
              fill="none"
              stroke={finalStrokeColor}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <C1_KitchenApplianceSymbol kind={iconKind} />
            </svg>
            <text
              x={width / 2}
              y={height * 0.77}
              fill={finalStrokeColor}
              fontSize="11"
              fontWeight="600"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {label}
            </text>
          </C1_FurnitureUprightContent>
        </C1_FurnitureSvg>
      );
    },
  };
}
